import {
  DatePicker,
  Divider,
  Form,
  Input,
  Typography,
  Space,
  Button,
} from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ModelSelectOption from "@/components/Elements/ModelSelectOption";
import Coordinates from "@/components/Form/Coordinates";
import { ASSET_FILES_UPLOAD_MAX_COUNT } from "@/entities/asset";
import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import {
  AssetConditionRatingField,
  getAssetConditionRatingMutateValue,
} from "@/modules/asset/asset-condition-rating-field";
import {
  useAssetFormAssetType,
  useAssetFormServiceDate,
} from "@/modules/asset/asset-form/asset-form.hook";
import AssetReplacementCostField from "@/modules/asset/asset-replacement-cost-field";
import {
  AssetRiskField,
  getAssetRiskMutateValue,
} from "@/modules/asset/asset-risk-field";
import { AssetTypeSelect } from "@/modules/asset-type/asset-type-select";
import AssetsSelectParent from "@/modules/assets/assets-select-parent";
import DocumentUploadField, {
  getSingleDocumentUploadFieldValue,
} from "@/modules/document/document-upload-field";
import DocumentsUploadField, {
  getMultipleDocumentsUploadFieldValue,
} from "@/modules/documents/documents-upload-field";
import { useFormValidation } from "@/modules/form-validation";
import { InterventionTemplateSelect } from "@/modules/intervention-template/intervention-template-select/intervention-template-select.component";
import { getInterventionTemplateId } from "@/modules/intervention-template/intervention-template-select/intervention-template-select.helper";
import LocationSelectParent from "@/modules/location/location-select-parent";
import OrganizationSelect from "@/modules/organization/organization-select";
import QueryableSelect from "@/modules/queryable-select";
import { getSelectSingleValue } from "@/modules/select";
import { SidePanelFooter } from "@/modules/side-panel";
import { DocumentType } from "@/propTypes";
import { FormItemAlert } from "@/ui/form-item-alert";
import { InputNumber } from "@/ui/input-number";
import { toISOString, yearsToDays } from "@/utilities/date";

import { useParentAssetCoordinates } from "./hooks/useParentAssetCoordinates";

const { useForm } = Form;
const { Title } = Typography;

const AssetForm = ({
  model,
  mutation,
  saveButtonLabel,
  initialValues,
  onMutate,
}) => {
  const [form] = useForm();
  const { t } = useTranslation();

  const {
    assetTypeId,
    setAssetTypeId,
    isAssetTypeLoading,
    showAssetTypeAlert,
  } = useAssetFormAssetType(model);

  const serviceDate = useAssetFormServiceDate(form);

  const [selectedOrganization, setSelectedOrganization] = useState();
  const [selectedLocation, setSelectedLocation] = useState();
  const parentAssetCoordinates = useParentAssetCoordinates(form);
  const isEdit = model && model?.id;

  useEffect(() => {
    if (parentAssetCoordinates && !form.getFieldValue("coordinate")) {
      form.setFieldsValue({ coordinate: parentAssetCoordinates });
    }
  }, [parentAssetCoordinates]);

  const setInitialValues = (initialValues) => {
    setSelectedOrganization(
      initialValues.organization?.id ?? initialValues.organization?.value
    );
    setSelectedLocation(
      initialValues.location?.id ?? initialValues.location?.value
    );
  };

  useEffect(() => {
    if (initialValues) setInitialValues(initialValues);
  }, []);

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([onClose, onMutate]);

  useGenericFormErrorNotification(mutation.isError);
  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    [
      "name",
      "asset_type_id",
      "reference",
      "description",
      "manufacturer",
      "model",
      "serial",
      "date_manufacture",
      "date_service",
      "replacement_cost",
      "theoretical_lifespan_in_days",
      "asset_info",
      "technical_info",
      "cartesian_image",
      "coordinate",
      "organization_id",
      "location_id",
      "asset_id",
      "severity_grid_id",
      "condition_rating",
      "risk_level",
      "files",
      "cmms_id",
      "erp_id",
      "study_frequency_in_month",
      "attendance_token",
      "plan_file",
    ]
  );

  const onSave = (formValues) => {
    const data = {
      name: form.getFieldValue("name"),
      asset_type_id: getSelectSingleValue(form, "assetType"),
      reference: form.getFieldValue("referenceNumber"),
      description: form.getFieldValue("description"),
      manufacturer: form.getFieldValue("manufacturer"),
      model: form.getFieldValue("model"),
      serial: form.getFieldValue("serialNumber"),
      date_manufacture: toISOString(form.getFieldValue("manufactureDate")),
      date_service: toISOString(form.getFieldValue("serviceDate")),
      replacement_cost: form.getFieldValue("replacementCost"),
      theoretical_lifespan_in_days: yearsToDays(
        form.getFieldValue("theoreticalLifespan")
      ),
      asset_info: form.getFieldValue("assetInfo"),
      technical_info: form.getFieldValue("technicalInfo"),
      cartesian_image: getSingleDocumentUploadFieldValue({
        formValue: formValues.cartesian_image,
        defaultDocument: model?.cartesian_image,
      }),
      coordinate: form.getFieldValue("coordinate"),
      location_id: getSelectSingleValue(form, "location"),
      asset_id: getSelectSingleValue(form, "asset", null),
      severity_grid_id: getSelectSingleValue(form, "severityGrid"),
      condition_rating: getAssetConditionRatingMutateValue(
        formValues.conditionRating
      ),
      risk_level: getAssetRiskMutateValue(formValues.risk),
      files: getMultipleDocumentsUploadFieldValue({
        formValue: formValues.assetFiles,
        defaultDocuments: model?.files,
      }),
      cmms_id: form.getFieldValue("cmmsId"),
      erp_id: form.getFieldValue("erpId"),
      study_frequency_in_month: form.getFieldValue("studyFrequency"),
      attendance_token: form.getFieldValue("attendanceToken"),
      plan_file: getSingleDocumentUploadFieldValue({
        formValue: formValues.plan_file,
        defaultDocument: model?.plan_file,
      }),
      intervention_template_id: getInterventionTemplateId(formValues.template),
    };

    mutation.mutate(data, { onSuccess: onMutateSuccess });
  };

  const handleOrgChange = (org) => {
    setSelectedOrganization(org);
    setSelectedLocation(undefined);
    form.setFieldsValue({ location: undefined, asset: undefined });
  };

  const handleLocChange = (loc) => {
    setSelectedLocation(loc);
    form.setFieldsValue({ asset: undefined });
  };

  const handleAssetTypeChange = (assetType) => {
    setAssetTypeId(assetType);
    form.setFieldsValue({ severityGrid: undefined });
  };

  return (
    <Form
      id="assetForm"
      form={form}
      layout="vertical"
      autoComplete="off"
      initialValues={initialValues}
      onFinish={onSave}
      scrollToFirstError={{
        behavior: "smooth",
        block: "center",
        inline: "center",
      }}
      role="form"
    >
      <Title level={3}>
        {model?.asset_id || initialValues.asset?.id ? (
          <span>{t("asset:form.associateAssetWith.component")}</span>
        ) : (
          <span>{t("asset:form.associateAssetWith.asset")}</span>
        )}
      </Title>
      <OrganizationSelect
        onChange={handleOrgChange}
        disabled={isEdit}
        {...validation("organization_id")}
      />
      <LocationSelectParent
        selectedOrganization={selectedOrganization}
        onChange={handleLocChange}
        rules={[{ required: true }]}
        {...validation("location_id")}
      />
      <AssetsSelectParent
        label={t("asset:form.parent.label")}
        placeholder={t("asset:form.parent.label")}
        currentAssetId={model?.id}
        selectedLocation={selectedLocation}
        {...validation("asset_id")}
      />
      <Divider />
      <Title level={3}>
        {model?.asset_id || initialValues.asset?.value ? (
          <span>{t("asset:form.informations.component")}</span>
        ) : (
          <span>{t("asset:form.informations.asset")}</span>
        )}
      </Title>
      <Form.Item
        name="name"
        label={t("asset:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input placeholder={t("asset:form.name.label")} data-testid="name" />
      </Form.Item>
      <AssetTypeSelect
        name="assetType"
        rules={[{ required: true }]}
        onChange={handleAssetTypeChange}
        loading={isAssetTypeLoading}
        {...validation("asset_type_id")}
      />
      {showAssetTypeAlert && (
        <FormItemAlert title={t("asset:form.assetType.alert")} />
      )}
      <Form.Item
        name="studyFrequency"
        label={t("asset:form.studyFrequency.label")}
        tooltip={t("asset:form.studyFrequency.tooltip")}
        rules={[{ required: true }]}
        {...validation("study_frequency_in_month")}
      >
        <InputNumber
          data-testid="study_frequency"
          min={1}
          addonAfter={t("asset:form.studyFrequency.unit")}
        />
      </Form.Item>
      <Form.Item
        name="coordinate"
        label={t("global:coordinate.label")}
        rules={[{ required: true }]}
        {...validation("coordinate")}
      >
        <Coordinates
          mapId={`asset_form_${model?.id}`}
          allowClear={true}
          sizes={{ height: 200 }}
          onCoordinateChange={(lat, lng) =>
            form.setFieldsValue({ coordinate: `${lat},${lng}` })
          }
          onClear={() => form.setFieldsValue({ coordinate: null })}
        />
      </Form.Item>
      <AssetConditionRatingField
        name="conditionRating"
        label={t("asset:form.conditionRating.label")}
        placeholder={t("asset:form.conditionRating.label")}
        {...validation("condition_rating")}
      />
      <AssetRiskField
        name="risk"
        label={t("asset:risk.label")}
        placeholder={t("asset:risk.label")}
        {...validation("risk_level")}
      />
      <Form.Item
        name="referenceNumber"
        label={t("asset:form.referenceNumber.label")}
        {...validation("reference")}
      >
        <Input
          placeholder={t("asset:form.referenceNumber.isOptionalMessage")}
          data-testid="reference"
        />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("asset:form.description.label")}
        {...validation("description")}
      >
        <Input
          placeholder={t("asset:form.description.label")}
          data-testid="description"
        />
      </Form.Item>
      <QueryableSelect
        filterBy="name"
        optionFormat={ModelSelectOption}
        model="severityGrids"
        fields={["id", "name"]}
        dataTestId="severityGrid"
        placeholder={t("asset:form.severityGrid.label")}
        name="severityGrid"
        label={t("asset:form.severityGrid.label")}
        rootId={assetTypeId}
        disabled={!assetTypeId}
        hiddenIfEmpty
        {...validation("severity_grid_id")}
      />
      <Form.Item
        name="manufacturer"
        label={t("asset:form.manufacturer.label")}
        {...validation("manufacturer")}
      >
        <Input
          placeholder={t("asset:form.manufacturer.label")}
          data-testid="manufacturer"
        />
      </Form.Item>
      <Form.Item
        name="model"
        label={t("asset:form.model.label")}
        {...validation("model")}
      >
        <Input placeholder={t("asset:form.model.label")} data-testid="model" />
      </Form.Item>
      <Form.Item
        name="serialNumber"
        label={t("asset:form.serialNumber.label")}
        {...validation("serial")}
      >
        <Input
          placeholder={t("asset:form.serialNumber.label")}
          data-testid="serial"
        />
      </Form.Item>
      <Form.Item
        name="manufactureDate"
        label={t("asset:form.manufactureDate.label")}
        {...validation("date_manufacture")}
      >
        <DatePicker data-testid="date_manufacture" />
      </Form.Item>
      <Form.Item
        name="serviceDate"
        label={t("asset:form.serviceDate.label")}
        {...validation("date_service")}
        dependencies={serviceDate.dependencies}
        rules={serviceDate.rules}
      >
        <DatePicker data-testid="date_service" minDate={serviceDate.minDate} />
      </Form.Item>
      <AssetReplacementCostField
        name="replacementCost"
        {...validation("replacement_cost")}
      />
      <Form.Item
        name="theoreticalLifespan"
        label={t("asset:form.theoreticalLifespan.label")}
        tooltip={t("asset:form.theoreticalLifespan.tooltip")}
        {...validation("theoretical_lifespan_in_days")}
      >
        <InputNumber
          placeholder={t("asset:form.theoreticalLifespan.label")}
          data-testid="theoretical_lifespan_in_years"
          min={0}
          addonAfter={t("asset:form.theoreticalLifespan.unit")}
        />
      </Form.Item>
      <Form.Item
        name="assetInfo"
        label={t("asset:form.assetInfo.label")}
        {...validation("asset_info")}
      >
        <Input.TextArea data-testid="asset_info" />
      </Form.Item>
      <Form.Item
        name="technicalInfo"
        label={t("asset:form.technicalInfo.label")}
        {...validation("technical_info")}
      >
        <Input.TextArea data-testid="technical_info" />
      </Form.Item>
      <DocumentUploadField
        name="cartesian_image"
        label={t("asset:form.assetPicture.label")}
        document={model?.cartesian_image}
        uploadProps={{
          type: "image",
          translationType: "photo",
          "data-testid": "upload-cartesian-image",
        }}
        {...validation("cartesian_image")}
      />
      <DocumentUploadField
        name="plan_file"
        label={t("asset:form.planFile.label")}
        document={model?.plan_file}
        uploadProps={{
          fileTypes: [".pdf"],
          layout: "button",
          "data-testid": "upload-plan-file",
        }}
        {...validation("plan_file")}
      />
      <DocumentsUploadField
        name="assetFiles"
        label={t("asset:form.assetFiles.label")}
        documents={model?.files}
        uploadProps={{
          maxCount: ASSET_FILES_UPLOAD_MAX_COUNT,
          "data-testid": "upload-asset-files",
        }}
        {...validation("files")}
      />
      <Form.Item
        name="cmmsId"
        label={t("asset:form.cmmsId.label")}
        {...validation("cmms_id")}
      >
        <Input
          placeholder={t("asset:form.cmmsId.label")}
          data-testid="cmms_id"
        />
      </Form.Item>
      <Form.Item
        name="erpId"
        label={t("asset:form.erpId.label")}
        {...validation("erp_id")}
      >
        <Input placeholder={t("asset:form.erpId.label")} data-testid="erp_id" />
      </Form.Item>
      <Form.Item
        name="attendanceToken"
        label={t("asset:form.attendanceToken.label")}
        {...validation("attendance_token")}
      >
        <Input.Password
          placeholder={t("asset:form.attendanceToken.label")}
          data-testid="attendance_token"
          autoComplete="new-password" // some browsers don't support 'off' yet @see https://gist.github.com/niksumeiko/360164708c3b326bd1c8
          iconRender={() => null}
        />
      </Form.Item>

      <InterventionTemplateSelect
        name="template"
        {...validation("intervention_template_id")}
      />

      {validationErrors().others && <Form.Item {...validation("others")} />}

      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="assetForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

const modelShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  organization: PropTypes.object,
  location: PropTypes.object,
  ancestors: PropTypes.arrayOf(PropTypes.object),
  cartesian_image: DocumentType,
  plan_file: DocumentType,
  geographical_data: PropTypes.object,
  condition_rating: PropTypes.string,
  asset_id: PropTypes.number,
});

const initialValuesShape = PropTypes.shape({
  name: PropTypes.string,
  organization: PropTypes.object,
  location: PropTypes.object,
  asset: PropTypes.object,
  assetType: PropTypes.object,
  studyFrequency: PropTypes.number,
  files: PropTypes.array,
});

AssetForm.propTypes = {
  model: modelShape,
  mutation: PropTypes.object.isRequired,
  saveButtonLabel: PropTypes.string.isRequired,
  initialValues: initialValuesShape.isRequired,
  onMutate: PropTypes.func,
};

export default AssetForm;
